/**
 * Lets the legacy `@workwhile/ui` styles run at the browser's default root
 * font size so `@workwhile/web-ui` can be used unmodified alongside it. It
 * rewrites legacy's styled-components CSS in two ways:
 *
 * 1. Rem scaling. Legacy was written for `html { font-size: 62.5% }`
 *    (1rem = 10px). That declaration is dropped and every rem is scaled by
 *    10/16, so legacy renders as before at 1rem = 16px.
 * 2. Reset layering. Legacy's global element rules (the CSS reset,
 *    `button:focus`, ...) are wrapped in `@layer legacy-reset`, which
 *    `legacy-ui-compat.css` orders below web-ui. Unlayered they'd beat every
 *    web-ui style. `body` stays unlayered so legacy keeps its document font
 *    size and line height.
 *
 * CSS that doesn't go through styled-components (inline styles, react-select's
 * emotion styles, bundled third-party CSS) is handled in `legacy-ui-compat.css`.
 *
 * Remove this along with `@workwhile/ui`.
 */

const RESET_LAYER = 'legacy-reset'

const REM_SCALE = 10 / 16

// A rem amount that isn't part of an identifier, eg `1.4rem` or `-.5rem`
const REM = /(?<![\w.-])(-?(?:\d+\.?\d*|\.\d+))rem\b/g

// An element or `*` with optional pseudos, eg `button:focus`, `*::before` or
// `a:not([href])`
const ELEMENT = String.raw`(\*|[a-z][a-z0-9]*)(::?[a-z-]+(\(\[[^\]]*\]\))?)*`

// Elements joined by combinators, eg `a:not([href])>*`. No classes or ids, so
// it can't be a component's styles
const ELEMENT_SELECTOR = new RegExp(`^${ELEMENT}(\\s*[>+~ ]\\s*${ELEMENT})*$`)

// The parts of a stylis AST node used here, to avoid depending on stylis
interface StylisElement {
  type: string
  // The printed node, eg `color:red;` or `@layer name`
  value: string
  // A rule's selectors, or a declaration's property
  props: string | string[]
  // A rule's nodes, or a declaration's value
  children: string | StylisElement[]
  // The enclosing rule or at-rule, null at the top level
  root: StylisElement | null
  parent: StylisElement | null
  length: number
}

const scaleRems = (value: string) =>
  value.replace(REM, (_, amount) => {
    const scaled = Number(amount) * REM_SCALE
    return `${Number(scaled.toFixed(4))}rem`
  })

const rewriteDeclaration = (declaration: StylisElement) => {
  const { props: property, children: value, root: rule } = declaration
  if (typeof value !== 'string') {
    return
  }

  const isRootFontSize =
    property === 'font-size' &&
    value === '62.5%' &&
    rule !== null &&
    Array.isArray(rule.props) &&
    rule.props.includes('html')
  if (isRootFontSize) {
    declaration.value = ''
    return
  }

  if (value.includes('rem')) {
    declaration.children = scaleRems(value)
    declaration.value = `${property}:${declaration.children};`
  }
}

const isGlobalElementRule = (rule: StylisElement) => {
  const selectors = rule.props
  // Keyframe selectors like `from` and `to` look like elements
  if (!Array.isArray(selectors) || rule.root?.type === '@keyframes') {
    return false
  }
  if (selectors.length === 1 && selectors[0] === 'body') {
    return false
  }
  return selectors.every((selector) => ELEMENT_SELECTOR.test(selector))
}

// stylis can't insert nodes from a plugin, so the rule turns into the @layer
// in place and a copy of it becomes the layer's only child. The copy and its
// declarations point their `root` at the layer, which keeps the copy from
// also being printed at the top level and stops it being wrapped again.
const wrapInResetLayer = (rule: StylisElement) => {
  const copy: StylisElement = { ...rule, root: rule, parent: rule }
  if (Array.isArray(copy.children)) {
    copy.children.forEach((child) => {
      child.root = copy
      child.parent = copy
    })
  }
  Object.assign(rule, {
    type: '@layer',
    value: `@layer ${RESET_LAYER}`,
    props: [RESET_LAYER],
    children: [copy],
    length: 1,
  })
}

const handleElement = (element: StylisElement) => {
  if (element.type === 'decl') {
    rewriteDeclaration(element)
    return
  }

  const isInLayer = element.root?.type === '@layer'
  if (element.type === 'rule' && !isInLayer && isGlobalElementRule(element)) {
    wrapInResetLayer(element)
  }
}

export const legacyUiStylisPlugin = Object.defineProperty(
  handleElement,
  'name',
  { value: 'legacy-ui-compat' }
)
